/**
 * AuthLayout — shell for /login, /register, /pending.
 *
 * Centered card on top of a quiet ink background. Footer is intentionally
 * sparse — just status pulse + copyright (no Paradigma mention, no
 * internal build metadata).
 */
import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Logo } from '@/components/ui/Logo'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'

interface AuthLayoutProps {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  const { t } = useTranslation('common')
  return (
    <div className="min-h-screen flex flex-col bg-ink">
      {/* Top brand strip + language picker */}
      <header className="border-b border-line px-6 h-14 flex items-center">
        <Link to="/" className="hover:opacity-90">
          <Logo />
        </Link>
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </header>

      {/* Centered card */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {subtitle && (
              <p className="mt-1.5 text-sm text-textDim">{subtitle}</p>
            )}
          </div>
          <div className="bg-surface border border-line rounded-md p-6">
            {children}
          </div>
          {footer && (
            <div className="mt-6 text-center text-sm text-textDim">{footer}</div>
          )}
        </div>
      </main>

      {/* Footer strip */}
      <footer className="px-6 py-3 border-t border-line text-textFaint text-xs font-mono flex items-center">
        <span className="dot mr-2" style={{ background: 'var(--pulse)' }} />
        {t('status.operational')}
        <span className="ml-auto">{t('footer.copyright')}</span>
      </footer>
    </div>
  )
}
